import { useEffect, useMemo, useState } from 'react'
import { CheckCheck, Eye, ListChecks, MessageCircle, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import { useMessages } from '../hooks/useActivityFeed'
import { kanbify } from '../lib/kanbifyClient'
import EmptyState from './ui/EmptyState'

export default function MessageCenter({ onOpenCard }) {
    const { t } = useTranslation()
    const { user } = useAuth()
    const { notifications: messages, unreadCount, markRead, markAllRead } = useMessages(user?.id)
    const [open, setOpen] = useState(false)
    const [cardsById, setCardsById] = useState({})
    const [secondaryMemberCards, setSecondaryMemberCards] = useState([])

    useEffect(() => {
        if (!user?.id) {
            setSecondaryMemberCards([])
            return undefined
        }

        let cancelled = false
        const fetchSecondaryMemberCards = async () => {
            const { data: myMemberships } = await kanbify
                .from('card_members')
                .select('card_id')
                .eq('user_id', user.id)
            const cardIds = [...new Set((myMemberships || []).map((row) => row.card_id).filter(Boolean))]
            if (!cardIds.length) {
                if (!cancelled) setSecondaryMemberCards([])
                return
            }

            const [{ data: cards }, { data: memberships }] = await Promise.all([
                kanbify.from('cards').select('id, title, claimed_by, updated_at').in('id', cardIds),
                kanbify.from('card_members').select('card_id, user_id').in('card_id', cardIds),
            ])
            if (cancelled) return

            const memberCountByCard = new Map()
            for (const membership of memberships || []) {
                memberCountByCard.set(membership.card_id, (memberCountByCard.get(membership.card_id) || 0) + 1)
            }
            const nextCards = (cards || []).filter((card) => (
                (memberCountByCard.get(card.id) || 0) > 1
                && (!card.claimed_by || card.claimed_by !== user.id)
            ))
            setSecondaryMemberCards((current) => {
                const signature = (rows) => rows
                    .map((card) => `${card.id}:${card.claimed_by || ''}:${card.updated_at || ''}`)
                    .sort()
                    .join('|')
                return signature(current) === signature(nextCards) ? current : nextCards
            })
        }

        void fetchSecondaryMemberCards()
        const intervalId = window.setInterval(() => { void fetchSecondaryMemberCards() }, 4000)
        return () => {
            cancelled = true
            window.clearInterval(intervalId)
        }
    }, [user?.id])

    const cardGroups = useMemo(() => {
        const grouped = new Map()
        messages.forEach((message) => {
            if (!message?.card_id) return
            const current = grouped.get(message.card_id) || {
                cardId: message.card_id,
                rows: [],
                hasWatch: false,
                hasMention: false,
                latestAt: '',
            }
            current.rows.push(message)
            current.hasWatch ||= message.type === 'card_watch'
            current.hasMention ||= message.type === 'mention'
            if (!current.latestAt || String(message.created_at || '') > current.latestAt) {
                current.latestAt = String(message.created_at || '')
            }
            grouped.set(message.card_id, current)
        })

        secondaryMemberCards.forEach((card) => {
            const current = grouped.get(card.id) || {
                cardId: card.id,
                rows: [],
                hasWatch: false,
                hasMention: false,
                hasSecondaryAssignment: false,
                latestAt: '',
            }
            current.hasSecondaryAssignment = true
            if (!current.latestAt) current.latestAt = String(card.updated_at || '')
            grouped.set(card.id, current)
        })

        return Array.from(grouped.values())
            .filter((group) => group.hasWatch || group.hasMention || group.hasSecondaryAssignment)
            .map((group) => ({
                ...group,
                unreadRows: group.rows.filter((message) => message.type !== 'card_watch' && !message.is_read),
            }))
            .sort((a, b) => b.latestAt.localeCompare(a.latestAt))
    }, [messages, secondaryMemberCards])

    const cardIdsKey = cardGroups.map((group) => group.cardId).sort().join('|')

    useEffect(() => {
        const cardIds = cardIdsKey ? cardIdsKey.split('|') : []
        if (!cardIds.length) {
            setCardsById({})
            return
        }

        let cancelled = false
        const secondaryCardsById = Object.fromEntries(secondaryMemberCards.map((card) => [card.id, card]))
        kanbify
            .from('cards')
            .select('id, title')
            .in('id', cardIds)
            .then(({ data }) => {
                if (cancelled) return
                setCardsById({
                    ...secondaryCardsById,
                    ...Object.fromEntries((data || []).map((card) => [card.id, card])),
                })
            })

        return () => { cancelled = true }
    }, [cardIdsKey, secondaryMemberCards])

    const openCard = async (group) => {
        if (!group?.cardId) return
        await Promise.all(group.unreadRows.map((message) => markRead(message.id)))
        setOpen(false)
        if (onOpenCard) {
            await onOpenCard(group.cardId, { preload: true, focus: 'comments' })
        }
    }

    return (
        <div className="message-center">
            <button
                type="button"
                className={`message-center-button${unreadCount ? ' has-unread' : ''}`}
                onClick={() => setOpen((value) => !value)}
                aria-expanded={open}
                aria-label={t('messages.open')}
                title={t('messages.title')}
            >
                <Eye size={22} />
                {unreadCount > 0 ? <span>{unreadCount > 99 ? '99+' : unreadCount}</span> : null}
            </button>

            {open ? (
                <>
                    <div className="notif-backdrop" onClick={() => setOpen(false)} />
                    <section className="notif-panel message-center-panel" aria-label={t('messages.title')}>
                        <div className="notif-header">
                            <div className="notif-title-group">
                                <span className="notif-title">{t('messages.title')}</span>
                                {cardGroups.length ? <span className="notif-count">({cardGroups.length})</span> : null}
                            </div>
                            <div className="notif-actions">
                                {unreadCount > 0 ? (
                                    <button type="button" className="notif-mark-all" onClick={markAllRead}>
                                        <CheckCheck size={13} /> {t('messages.markAllRead')}
                                    </button>
                                ) : null}
                                <button type="button" className="notif-close" onClick={() => setOpen(false)} aria-label={t('common.close')}>
                                    <X size={16} />
                                </button>
                            </div>
                        </div>
                        <div className="notif-list">
                            {!cardGroups.length ? (
                                <EmptyState
                                    icon={<MessageCircle size={40} style={{ color: 'var(--text-muted)', opacity: 0.45 }} />}
                                    title={t('messages.emptyTitle')}
                                    description={t('messages.emptyDescription')}
                                />
                            ) : cardGroups.map((group) => (
                                <button
                                    type="button"
                                    key={group.cardId}
                                    className={`notif-item message-center-item${group.unreadRows.length ? ' unread' : ''}`}
                                    onClick={() => { void openCard(group) }}
                                >
                                    <span className="notif-icon"><ListChecks size={16} /></span>
                                    <span className="notif-content">
                                        <strong className="notif-item-title">
                                            {cardsById[group.cardId]?.title || t('messages.cardFallback', { defaultValue: 'Card' })}
                                        </strong>
                                    </span>
                                    {group.unreadRows.length ? (
                                        <span className="message-card-unread">{group.unreadRows.length > 99 ? '99+' : group.unreadRows.length}</span>
                                    ) : null}
                                </button>
                            ))}
                        </div>
                    </section>
                </>
            ) : null}
        </div>
    )
}
